import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api } from "@/lib/api";

const WINDOW_META = [
  { days: 30,  label: "1M",  color: "#F59E0B" },
  { days: 90,  label: "3M",  color: "#14B8A6" },
  { days: 180, label: "6M",  color: "#0B3C5D" },
];

export default function RollingReturnsChart({ code }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    const windows = WINDOW_META.map((w) => w.days).join(",");
    api.get(`/funds/${code}/rolling-returns-series`, { params: { windows } })
      .then((r) => setData(r.data));
  }, [code]);

  const points = data?.points || [];

  const stats = useMemo(() => {
    if (!points.length) return null;
    const out = {};
    for (const w of WINDOW_META) {
      const vals = points.map((p) => p[`r_${w.days}`]).filter((v) => v != null);
      if (!vals.length) continue;
      out[w.days] = {
        avg: (vals.reduce((s, x) => s + x, 0) / vals.length).toFixed(2),
        max: Math.max(...vals).toFixed(2),
        min: Math.min(...vals).toFixed(2),
      };
    }
    return out;
  }, [points]);

  return (
    <section
      data-testid="rolling-series-section"
      className="bg-white border border-sif rounded-xl p-4 sm:p-6 card-shadow mb-6"
    >
      <div className="mb-4">
        <div className="text-xs text-sif-muted uppercase tracking-wider font-semibold">Rolling Returns Over Time</div>
        <div className="text-base sm:text-lg font-semibold text-sif-primary mt-0.5">
          1M / 3M / 6M return of every day since inception
        </div>
      </div>

      <div className="h-[240px] sm:h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={points} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: "#64748B" }}
              stroke="#CBD5E1"
              minTickGap={30}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#64748B" }}
              stroke="#CBD5E1"
              tickFormatter={(v) => `${v}%`}
            />
            <Tooltip
              contentStyle={{
                background: "white",
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                fontSize: "12px",
              }}
              formatter={(v) => (v != null ? `${v}%` : "—")}
            />
            <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }} />
            <ReferenceLine y={0} stroke="#94A3B8" strokeDasharray="4 4" />
            {WINDOW_META.map((w) => (
              <Line
                key={w.days}
                type="monotone"
                dataKey={`r_${w.days}`}
                name={`${w.label} return`}
                stroke={w.color}
                strokeWidth={1.8}
                dot={false}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {stats && (
        <div className="mt-4 sm:mt-5 pt-4 border-t border-sif grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          {WINDOW_META.map((w) => (
            <div key={w.days} data-testid={`rr-stats-${w.days}`} className="bg-slate-50/50 p-2.5 sm:p-0 rounded-lg sm:bg-transparent">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: w.color }} />
                <span className="text-[11px] uppercase tracking-wider text-sif-muted font-semibold">{w.label}</span>
              </div>
              <div className="text-xs text-sif-muted flex justify-between">
                <span>Avg</span>
                <span className="font-mono font-semibold text-sif-primary">{stats[w.days]?.avg}%</span>
              </div>
              <div className="text-xs text-sif-muted flex justify-between">
                <span>Max</span>
                <span className="font-mono font-semibold gain">{stats[w.days]?.max}%</span>
              </div>
              <div className="text-xs text-sif-muted flex justify-between">
                <span>Min</span>
                <span className="font-mono font-semibold loss">{stats[w.days]?.min}%</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
